import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetTemplates } from '../../../../queries/signageTemplates';

/**
 * Warns when a wall/group is about to play (or is playing) a template that was
 * DESIGNED for the opposite orientation — e.g. a portrait menu on a landscape wall.
 * Templates are reusable and assigned indirectly (Library playlist → wall), so the
 * mismatch can only be detected here, where the template's designed orientation
 * (settings.orientation) meets the wall's physical orientation.
 *
 * Props:
 *   items:          playlist items (the effective/assigned playlist's items)
 *   wallOrientation: 'portrait' | 'landscape'
 */
export default function TemplateOrientationWarning({ items, wallOrientation }) {
  const { t } = useTranslation();
  const { data: templates = [] } = useGetTemplates();

  const byId = useMemo(() => {
    const m = {};
    templates.forEach((tpl) => { m[tpl.id] = tpl; });
    return m;
  }, [templates]);

  const mismatched = useMemo(() => {
    if (!wallOrientation) return [];
    const tplOrientation = (tpl) => (tpl?.settings?.orientation === 'portrait' ? 'portrait' : 'landscape');
    return (items || [])
      .filter((it) => it?.content_type === 'template' && it?.template_instance_id)
      .map((it) => byId[it.template_instance_id])
      .filter(Boolean)
      .filter((tpl) => tplOrientation(tpl) !== wallOrientation);
  }, [items, byId, wallOrientation]);

  if (!mismatched.length) return null;
  const names = [...new Set(mismatched.map((tpl) => tpl.name))].join(', ');

  return (
    <div className="p-3 mb-3 d-flex align-items-start gap-2" style={{
      borderRadius: 'var(--pp-radius-lg)',
      background: 'rgba(245,158,11,0.12)',
      border: '1px solid var(--pp-warning, #f59e0b)' }}>
      <i className="pi pi-exclamation-triangle" style={{ color: 'var(--pp-warning, #f59e0b)', fontSize: 18, marginTop: 2 }} />
      <div className="small">
        <strong style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.templates.orientation_mismatch_title')}</strong>
        <div style={{ color: 'var(--pp-text-secondary)' }}>
          {t('video_wall.templates.orientation_mismatch_detail', {
            names,
            wall: t(`video_wall.templates.${wallOrientation}`),
          })}
        </div>
      </div>
    </div>
  );
}
